import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useTenantStore } from '../stores/tenantStore';
import { USER_TYPES } from '../utils/constants';
import { usePermissions } from '../hooks/usePermissions';
import Login from '../pages/auth/Login';
import AcceptInvite from '../pages/auth/AcceptInvite';
import EmailVerified from '../pages/auth/EmailVerified';
import ResetPassword from '../pages/auth/ResetPassword';
import DashboardLayout from '../layouts/DashboardLayout';
import Dashboard from '../pages/Dashboard';
import AppDevices from '../pages/AppDevices';
import AppDeviceDetail from '../pages/AppDeviceDetail';
import AppDeviceRegister from '../pages/AppDeviceRegister';
import AppDeviceGroups from '../pages/AppDeviceGroups';
import Edges from '../pages/Edges';
import EdgeDetail from '../pages/EdgeDetail';
import EdgeConfig from '../pages/EdgeConfig';
import MTGEs from '../pages/MTGEs';
import MTGEDetail from '../pages/MTGEDetail';
import MTGEConfig from '../pages/MTGEConfig';
import Connectors from '../pages/Connectors';
import DnsRecords from '../pages/DnsRecords';
import ConnectorDetail from '../pages/ConnectorDetail';
import ConnectorConfig from '../pages/ConnectorConfig';
import Routing from '../pages/Routing';
import EdgePeeringPage from '../pages/EdgePeering';
import Applications from '../pages/Applications';
import RoutingPolicies from '../pages/RoutingPolicies';
import DeviceStatus from '../pages/monitoring/DeviceStatus';
import Performance from '../pages/monitoring/Performance';
import Logs from '../pages/monitoring/Logs';
import DiagnosticTools from '../pages/monitoring/DiagnosticTools';
import ObservabilityDashboard from '../pages/monitoring/ObservabilityDashboard';
import ACLMonitoring from '../pages/monitoring/ACLMonitoring';
import SuricataMonitoring from '../pages/monitoring/SuricataMonitoring';
import TrafficMonitoring from '../pages/TrafficMonitoring';
import TrafficAnalytics from '../pages/analytics/TrafficAnalytics';
import UsageReports from '../pages/analytics/UsageReports';
import Users from '../pages/admin/Users';
import UserDetail from '../pages/admin/UserDetail';
import Companies from '../pages/admin/Companies';
import CompanyDetail from '../pages/admin/CompanyDetail';
import Channels from '../pages/admin/Channels';
import ChannelDetail from '../pages/admin/ChannelDetail';
import Tenants from '../pages/admin/Tenants';
import TenantDetail from '../pages/admin/TenantDetail';
import Roles from '../pages/admin/Roles';
import UserGroups from '../pages/admin/UserGroups';
import IdentityProviders from '../pages/admin/IdentityProviders';
import RoleDetail from '../pages/admin/RoleDetail';
import ConfigTemplates from '../pages/admin/ConfigTemplates';
import ConfigTemplateDetail from '../pages/admin/ConfigTemplateDetail';
import VPNUserDetail from '../pages/admin/VPNUserDetail';
import SystemSettings from '../pages/SystemSettings';
import AuditLog from '../pages/admin/AuditLog';
import UserProfile from '../pages/UserProfile';
import AccountSettingsPage from '../pages/AccountSettings';
import Notifications from '../pages/Notifications';
import MyTenant from '../pages/MyTenant';
import TenantAdminSharedGateways from '../pages/TenantAdminSharedGateways';
import NotFound from '../pages/NotFound';
// Access Platform (v3 ZTNA)
import AccessDashboard from '../pages/access/Dashboard';
import AccessActivity from '../pages/access/Activity';
import AccessSecurity from '../pages/access/Security';
import AccessApps from '../pages/access/Apps';
import MdmIntegrations from '../pages/access/MdmIntegrations';
import AccessPolicies from '../pages/access/Policies';
import AccessConnectors from '../pages/access/Connectors';
import AccessConnectorDetail from '../pages/access/ConnectorDetail';
import AccessConnectorGroupDetail from '../pages/access/ConnectorGroupDetail';
import AccessGateways from '../pages/access/Gateways';
import AccessGatewayDetail from '../pages/access/GatewayDetail';
import AccessDevices from '../pages/access/Devices';
import AccessPostureProfiles from '../pages/access/PostureProfiles';
import AccessSaasCatalog from '../pages/access/SaasCatalog';

/**
 * Keeps tenant-scoped pages out of reach when no concrete tenant is selected.
 *
 * Applies to both user types that have no tenant of their own: a SuperAdmin on
 * "All Tenants" and a ChannelAdmin who hasn't picked one yet. They land in
 * different places — a SuperAdmin's dashboard aggregates across tenants, while
 * a ChannelAdmin's "no tenant" home is the channel overview.
 */
const TenantGuard = ({ children }: { children: React.ReactElement }) => {
  const { user } = useAuthStore();
  const { getTenantId } = useTenantStore();

  if (!getTenantId()) {
    if (user?.type === USER_TYPES.CHANNEL_ADMIN) {
      return <Navigate to="/admin/channels" replace />;
    }
    if (user?.type === USER_TYPES.SUPER_ADMIN) {
      return <Navigate to="/dashboard" replace />;
    }
  }
  return children;
};

/** Redirects Viewer-role users away from the edge config page */
const RequireEdgeConfig = ({ children }: { children: React.ReactElement }) => {
  const { canUpdate } = usePermissions();
  const { id } = useParams<{ id: string }>();
  if (!canUpdate) {
    return <Navigate to={`/edges/${id}`} replace />;
  }
  return children;
};

/** Restricts route to SuperAdmin only */
const SuperAdminGuard = ({ children }: { children: React.ReactElement }) => {
  const { user } = useAuthStore();

  if (user?.type !== USER_TYPES.SUPER_ADMIN) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

/**
 * A ChannelAdmin with no tenant selected has nothing to see on the dashboard —
 * its summary endpoints are tenant-scoped and are among the API areas not yet
 * channel-aware, so they 403 rather than aggregate. Send them to their channel
 * overview instead. A SuperAdmin on "All Tenants" keeps today's behaviour.
 */
const DashboardGuard = ({ children }: { children: React.ReactElement }) => {
  const { user } = useAuthStore();
  const { getTenantId } = useTenantStore();

  if (user?.type === USER_TYPES.CHANNEL_ADMIN && !getTenantId()) {
    return <Navigate to="/admin/channels" replace />;
  }
  return children;
};

/**
 * Restricts a route to the channel-aware user types. SuperAdmin gets full CRUD
 * within the page; a ChannelAdmin gets a read-only view of their own channels.
 * That distinction is drawn inside the pages, not here — this guard only keeps
 * TenantAdmins and VPN users out, since /channels 403s for them anyway.
 */
const ChannelScopeGuard = ({ children }: { children: React.ReactElement }) => {
  const { user } = useAuthStore();

  if (user?.type !== USER_TYPES.SUPER_ADMIN && user?.type !== USER_TYPES.CHANNEL_ADMIN) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

function VpnUserPage() {
  const handleLogout = () => {
    useAuthStore.getState().logout();
    window.location.href = '/';
  };

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      minHeight: '100vh', backgroundColor: '#f8fafc', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      padding: '2rem',
    }}>
      <div style={{
        maxWidth: '480px', width: '100%', backgroundColor: 'white', borderRadius: '12px',
        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)',
        padding: '3rem 2.5rem', textAlign: 'center',
      }}>
        <div style={{ fontSize: '48px', marginBottom: '1.5rem' }}>
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#0ea5e9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          </svg>
        </div>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#0f172a', marginBottom: '0.75rem' }}>
          Continue on the SecureLink App
        </h1>
        <p style={{ color: '#64748b', lineHeight: 1.6, marginBottom: '1.5rem' }}>
          Your account is configured for VPN access via the <strong>SecureLinkVPN</strong> app.
          This management console is for platform administrators only.
        </p>
        <div style={{
          backgroundColor: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px',
          padding: '1rem 1.25rem', marginBottom: '1.5rem', textAlign: 'left',
        }}>
          <p style={{ color: '#0c4a6e', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>
            To connect:
          </p>
          <ol style={{ color: '#0369a1', fontSize: '0.875rem', lineHeight: 1.8, paddingLeft: '1.25rem', margin: 0 }}>
            <li>Open the <strong>SecureLinkVPN</strong> app on your device</li>
            <li>Sign in with the same email and password</li>
            <li>Select your assigned network and connect</li>
          </ol>
        </div>
        <p style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '1.5rem' }}>
          Don't have the app? Download it from the App Store or contact your administrator.
        </p>
        <button
          onClick={handleLogout}
          style={{
            backgroundColor: '#0ea5e9', color: 'white', border: 'none', borderRadius: '6px',
            padding: '0.625rem 1.5rem', fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer',
          }}
        >
          Sign Out
        </button>
      </div>
    </div>
  );
}

/** Redirect unauthenticated users to /login, preserving the attempted URL */
function ProtectedRoute({ children }: { children: React.ReactElement }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);
  const vpnUserBlocked = useAuthStore((state) => state.vpnUserBlocked);
  const location = useLocation();

  if (vpnUserBlocked) return <VpnUserPage />;

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public auth routes — reachable without a session */}
      <Route path="/login" element={<Login />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/email-verified" element={<EmailVerified />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Protected app — everything else requires a VSN+ session */}
      <Route path="/" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardGuard><Dashboard /></DashboardGuard>} />

        {/* Equipment (tenant-scoped) */}
        <Route path="edges" element={<TenantGuard><Edges /></TenantGuard>} />
        <Route path="edges/:id" element={<TenantGuard><EdgeDetail /></TenantGuard>} />
        <Route path="edges/:id/config" element={<TenantGuard><RequireEdgeConfig><EdgeConfig /></RequireEdgeConfig></TenantGuard>} />
        <Route path="mtges" element={<SuperAdminGuard><MTGEs /></SuperAdminGuard>} />
        <Route path="mtges/:id" element={<SuperAdminGuard><MTGEDetail /></SuperAdminGuard>} />
        <Route path="mtges/:id/config" element={<SuperAdminGuard><MTGEConfig /></SuperAdminGuard>} />
        <Route path="connectors" element={<TenantGuard><Connectors /></TenantGuard>} />
        <Route path="connectors/:id" element={<TenantGuard><ConnectorDetail /></TenantGuard>} />
        <Route path="connectors/:id/config" element={<TenantGuard><ConnectorConfig /></TenantGuard>} />
        <Route path="app-devices" element={<TenantGuard><AppDevices /></TenantGuard>} />
        <Route path="app-devices/register" element={<TenantGuard><AppDeviceRegister /></TenantGuard>} />
        <Route path="app-devices/:id" element={<TenantGuard><AppDeviceDetail /></TenantGuard>} />
        <Route path="app-device-groups" element={<TenantGuard><AppDeviceGroups /></TenantGuard>} />

        {/* Network (tenant-scoped) */}
        <Route path="routing" element={<TenantGuard><Routing /></TenantGuard>} />
        <Route path="dns" element={<TenantGuard><DnsRecords /></TenantGuard>} />
        <Route path="edge-peering" element={<TenantGuard><EdgePeeringPage /></TenantGuard>} />
        <Route path="routing-policies" element={<TenantGuard><RoutingPolicies /></TenantGuard>} />
        <Route path="applications" element={<TenantGuard><Applications /></TenantGuard>} />

        {/* Access Platform (v3 ZTNA) — Gateways is fleet infra, SuperAdmin-scoped like MTGEs */}
        {/* Observability surfaces (WP4): tenant-scoped by construction — every
            query and every record endpoint resolves the tenant server-side, so
            they are meaningless in All-Tenants mode. */}
        <Route path="access/dashboard" element={<TenantGuard><AccessDashboard /></TenantGuard>} />
        <Route path="access/activity" element={<TenantGuard><AccessActivity /></TenantGuard>} />
        <Route path="access/security" element={<TenantGuard><AccessSecurity /></TenantGuard>} />
        <Route path="access/apps" element={<TenantGuard><AccessApps /></TenantGuard>} />
        <Route path="access/policies" element={<TenantGuard><AccessPolicies /></TenantGuard>} />
        <Route path="access/connectors" element={<TenantGuard><AccessConnectors /></TenantGuard>} />
        <Route path="access/connectors/groups/:name" element={<TenantGuard><AccessConnectorGroupDetail /></TenantGuard>} />
        <Route path="access/connectors/:id" element={<TenantGuard><AccessConnectorDetail /></TenantGuard>} />
        <Route path="access/devices" element={<TenantGuard><AccessDevices /></TenantGuard>} />
        {/* Named device posture requirement sets that policies pick by name (V31). */}
        <Route path="access/posture-profiles" element={<TenantGuard><AccessPostureProfiles /></TenantGuard>} />
        {/* No guard: SuperAdmin needs the fleet view even in All-Tenants mode, and
            tenant admins get a read-only, API-scoped view of their assigned
            gateways (VSNP-1126). The API enforces scope either way. */}
        <Route path="access/gateways" element={<AccessGateways />} />
        <Route path="access/gateways/:id" element={<AccessGatewayDetail />} />
        {/* SaaS catalog is a GLOBAL, SuperAdmin-owned resource (VSNP-1150) */}
        <Route path="access/saas-catalog" element={<SuperAdminGuard><AccessSaasCatalog /></SuperAdminGuard>} />

        {/* Monitoring (tenant-scoped) */}
        <Route path="monitoring/status" element={<TenantGuard><DeviceStatus /></TenantGuard>} />
        <Route path="monitoring/performance" element={<TenantGuard><Performance /></TenantGuard>} />
        <Route path="monitoring/logs" element={<TenantGuard><Logs /></TenantGuard>} />
        <Route path="monitoring/diagnostic" element={<TenantGuard><DiagnosticTools /></TenantGuard>} />
        <Route path="monitoring/observability" element={<TenantGuard><ObservabilityDashboard /></TenantGuard>} />
        <Route path="monitoring/acl" element={<TenantGuard><ACLMonitoring /></TenantGuard>} />
        <Route path="monitoring/suricata" element={<TenantGuard><SuricataMonitoring /></TenantGuard>} />
        <Route path="monitoring/traffic" element={<TenantGuard><TrafficMonitoring /></TenantGuard>} />

        {/* Analytics (tenant-scoped) */}
        <Route path="analytics/traffic" element={<TenantGuard><TrafficAnalytics /></TenantGuard>} />
        <Route path="analytics/reports" element={<TenantGuard><UsageReports /></TenantGuard>} />

        {/* Administration */}
        <Route path="admin/users" element={<Users />} />
        <Route path="admin/users/:id" element={<UserDetail />} />
        {/* Identity groups for ZTNA policy "Who" are tenant-scoped (VSNP-1161):
            per-tenant CRUD, meaningless in All-Tenants mode, hence TenantGuard. */}
        <Route path="admin/user-groups" element={<TenantGuard><UserGroups /></TenantGuard>} />
        <Route path="admin/identity-providers" element={<TenantGuard><IdentityProviders /></TenantGuard>} />
        {/* Per-tenant MDM/EDR integrations (VSNP-1169) — an admin "connect your
            external system" task, so it sits with Identity Providers. */}
        <Route path="admin/mdm-integrations" element={<TenantGuard><MdmIntegrations /></TenantGuard>} />
        <Route path="admin/companies" element={<TenantGuard><Companies /></TenantGuard>} />
        <Route path="admin/companies/:id" element={<TenantGuard><CompanyDetail /></TenantGuard>} />
        {/* Channels — SuperAdmin manages, ChannelAdmin reads. This is also a
            ChannelAdmin's landing page when no tenant is selected. */}
        <Route path="admin/channels" element={<ChannelScopeGuard><Channels /></ChannelScopeGuard>} />
        <Route path="admin/channels/:id" element={<ChannelScopeGuard><ChannelDetail /></ChannelScopeGuard>} />
        {/* Tenants are managed by SuperAdmin and by a ChannelAdmin within their
            channel. GET/POST/PUT/DELETE /tenants are channel-scoped server-side,
            so a ChannelAdmin only ever sees and touches their own channel's
            tenants. Pages hide the few SuperAdmin-only affordances themselves. */}
        <Route path="admin/tenants" element={<ChannelScopeGuard><Tenants /></ChannelScopeGuard>} />
        <Route path="admin/tenants/:id" element={<ChannelScopeGuard><TenantDetail /></ChannelScopeGuard>} />
        <Route path="admin/roles" element={<TenantGuard><Roles /></TenantGuard>} />
        <Route path="admin/roles/:id" element={<TenantGuard><RoleDetail /></TenantGuard>} />
        <Route path="admin/config-templates" element={<TenantGuard><ConfigTemplates /></TenantGuard>} />
        <Route path="admin/config-templates/:id" element={<TenantGuard><ConfigTemplateDetail /></TenantGuard>} />
        <Route path="admin/vpn-users/:id" element={<VPNUserDetail />} />
        <Route path="admin/audit-log" element={<AuditLog />} />
        <Route path="admin/system" element={<SystemSettings />} />

        {/* Tenant Admin */}
        <Route path="my-tenant" element={<MyTenant />} />
        <Route path="shared-gateways" element={<TenantGuard><TenantAdminSharedGateways /></TenantGuard>} />

        {/* Settings & Profile */}
        <Route path="profile" element={<UserProfile />} />
        <Route path="profile/settings" element={<AccountSettingsPage />} />
        <Route path="notifications" element={<Notifications />} />

        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

export default AppRoutes;
